import { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import type { ConnectionStatus } from "@/hooks/useGallerySSE";

interface GalleryConnectionStatusProps {
  status: ConnectionStatus;
}

const PROLONGED_DISCONNECT_MS = 60_000;

const STATUS_COLORS: Record<ConnectionStatus, string> = {
  connecting: "#9ca3af",
  connected: "#22c55e",
  reconnecting: "#f59e0b",
  disconnected: "#ef4444",
};

export default function GalleryConnectionStatus({
  status,
}: GalleryConnectionStatusProps) {
  const shouldReduceMotion = useReducedMotion();
  const [showOverlay, setShowOverlay] = useState(false);
  const overlayTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (status === "reconnecting") {
      overlayTimerRef.current = setTimeout(() => {
        setShowOverlay(true);
      }, PROLONGED_DISCONNECT_MS);
    } else {
      setShowOverlay(false);
      if (overlayTimerRef.current) clearTimeout(overlayTimerRef.current);
    }
    return () => {
      if (overlayTimerRef.current) clearTimeout(overlayTimerRef.current);
    };
  }, [status]);

  const color = STATUS_COLORS[status];

  return (
    <>
      {/* Small dot indicator — bottom-left corner */}
      <Box
        component={shouldReduceMotion ? "div" : motion.div}
        {...(!shouldReduceMotion && status === "reconnecting"
          ? {
              animate: { scale: [1, 1.3, 1] },
              transition: { duration: 1.5, repeat: Infinity },
            }
          : {})}
        sx={{
          position: "fixed",
          bottom: 16,
          left: 16,
          width: 12,
          height: 12,
          borderRadius: "50%",
          bgcolor: color,
          zIndex: 1300,
          opacity: 0.8,
        }}
      />

      {/* Prolonged disconnection overlay */}
      <AnimatePresence>
        {showOverlay && (
          <Box
            component={motion.div}
            initial={shouldReduceMotion ? undefined : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={shouldReduceMotion ? undefined : { opacity: 0 }}
            transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.3 }}
            sx={{
              position: "fixed",
              inset: 0,
              bgcolor: "rgba(0, 0, 0, 0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1400,
            }}
          >
            <Typography
              variant="h5"
              sx={{ color: "rgba(255,255,255,0.8)", textAlign: "center", px: 4 }}
            >
              Connexion perdue — reconnexion en cours...
            </Typography>
          </Box>
        )}
      </AnimatePresence>
    </>
  );
}
